<template>
  <div class="team-color-picker">
    <div class="swatch-picker" :class="{ open: isOpenRef }">
      <button
        class="swatch center"
        :style="{ background: color }"
        @click="toggleWithSound"
      ></button>
      <button
        v-for="(sw, i) in altsRef"
        :key="i"
        class="swatch alt"
        :style="{ background: sw.color, transform: sw.transform }"
        @click="pick(i)"
      ></button>
    </div>
    <div class="picker-label">Team</div>
    <div class="picker-name">{{ name }}</div>
    <button class="picker-die" :class="{ rolling: rollingRef, hidden: !open }" @click="roll">
      <RefreshCwIcon/>
    </button>
  </div>
</template>

<style lang="scss" scoped>
.team-color-picker {
  width: 200px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}

.swatch-picker {
  position: relative;
  width: 100%;
  height: 160px;
}

.swatch {
  all: unset;
  position: absolute;
  top: 78px;
  left: 50%;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.35);
  cursor: pointer;
  transition: transform 0.35s ease, width 0.35s ease, height 0.35s ease, background-color 0.3s ease;
}

.swatch.alt {
  z-index: 1;
}

.swatch.center {
  width: 80px;
  height: 80px;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.35);
  z-index: 2;
  transform: translate(-50%, -50%) scale(1);
}

// shrink the team circle ~20% while choosing
.swatch-picker.open .swatch.center {
  width: 64px;
  height: 64px;
}

.picker-label {
  color: #888;
  line-height: 1;
  margin-bottom: -0.35rem;
  font-size: calc(1.75rem * 0.75); // 0.75x the team name below it
}

.picker-name {
  font-size: 1.75rem;
  text-align: center;
  line-height: 1.15;
  // reserve two lines so short vs wrapping names don't shove the die + Start button around
  min-height: 2.3em;
  display: flex;
  align-items: flex-start;
  justify-content: center;
}

.picker-die {
  all: unset;
  cursor: pointer;
  // on open, fade in slowly and a little late so the reroll control doesn't pull the eye
  opacity: 1;
  transition: opacity 0.6s ease 0.3s;

  svg {
    display: block;
    width: 2rem;
    height: 2rem;
    color: #888;
  }

  // hidden while closed. Uses opacity (not visibility) so it can fade, but still reserves space so
  // opening the picker doesn't shove the Start button down. Faster, undelayed fade-out on close.
  &.hidden {
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.2s ease;
  }
}
</style>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { RefreshCwIcon } from '@zhuowenli/vue-feather-icons'
import * as sounds from './matching/GameSounds'

const props = defineProps<{ color: string; name: string; open: boolean; hintDelay?: number }>()
const emit = defineEmits<{
  toggle: []
  'update:color': [color: string]
  'update:name': [name: string]
}>()

// the 8 alternate swatch positions around the centre (px offsets)
const altSlots = [
  { dx: -60, dy: -60 }, { dx: 0, dy: -60 }, { dx: 60, dy: -60 },
  { dx: -60, dy: 0 }, { dx: 60, dy: 0 },
  { dx: -60, dy: 60 }, { dx: 0, dy: 60 }, { dx: 60, dy: 60 },
]
// same transform-function shape as the slot transform, so scale(0) interpolates cleanly
const stackedTransform = 'translate(-50%, -50%) translate(0px, 0px) scale(0)'

// the team's 8-colour set persists across open/close so reopening shows the same options (and the
// currently-selected colour stays among them); only the die reroll replaces it
const colorSetRef = ref<{ color: string; name: string }[]>([])
const altsRef = ref<{ color: string; name: string; transform: string }[]>([])
const rollingRef = ref(false)

// the picker is "open" when the player opened it (prop) or the one-time affordance hint is running;
// both drive the same shrink-and-fan-out animation
const hintOpenRef = ref(false)
const isOpenRef = computed(() => props.open || hintOpenRef.value)

function hslToHex(h: number, s: number, l: number) {
  s /= 100
  l /= 100
  const a = s * Math.min(l, 1 - l)
  const channel = (n: number) => {
    const k = (n + h / 30) % 12
    const c = l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1))
    return Math.round(c * 255).toString(16).padStart(2, '0')
  }
  return `#${channel(0)}${channel(8)}${channel(4)}`
}

// wide but non-extreme colours: never washed-out grey or blown-out neon/near-white
function randomColor() {
  const h = Math.floor(Math.random() * 360)
  const s = 10 + Math.floor(Math.random() * 85)
  const l = 10 + Math.floor(Math.random() * 85)
  return hslToHex(h, s, l)
}

async function fetchColorNames(colors: string[]) {
  try {
    const values = colors.map(c => c.replace('#', '')).join(',')
    const response = await fetch(`https://api.color.pizza/v1/?values=${values}&list=bestOf`)
    const data = await response.json()
    return colors.map((c, i) => data.colors?.[i]?.name ?? c)
  } catch {
    return colors
  }
}

function slotTransform(i: number) {
  return `translate(-50%, -50%) translate(${altSlots[i].dx}px, ${altSlots[i].dy}px) scale(1)`
}

function makeColorSet() {
  const colors = Array.from({ length: 8 }, () => randomColor())
  colorSetRef.value = colors.map(color => ({ color, name: color }))
  fetchColorNames(colors).then(names => {
    colorSetRef.value = colorSetRef.value.map((s, i) => ({ ...s, name: names[i] }))
  })
}

// fly the (persistent) colour set out from behind the centre
function showAlts() {
  if (colorSetRef.value.length === 0) {
    makeColorSet()
  }
  // render stacked behind the centre, then (after a paint) fly out to the slots
  altsRef.value = colorSetRef.value.map(s => ({ ...s, transform: stackedTransform }))
  requestAnimationFrame(() => requestAnimationFrame(() => {
    altsRef.value = altsRef.value.map((s, i) => ({ ...s, transform: slotTransform(i) }))
  }))
}

watch(isOpenRef, (isOpen) => {
  if (isOpen) {
    showAlts()
  } else {
    altsRef.value = altsRef.value.map(s => ({ ...s, transform: stackedTransform }))
    setTimeout(() => { altsRef.value = [] }, 350)
  }
})

// one-time affordance hint: soon after the picker appears, run the normal open→close animation
// (centre circle shrinks, colours fan out, then back in) so players notice it's interactive. Driven
// through hintOpenRef so it reuses the exact open/close behaviour -- no sound, since sounds only fire
// on real taps. Skipped if the player opens the picker first. Both teams' pickers mount together, so
// the hints run in sync.
let hintOutTimer = 0
let hintInTimer = 0

onMounted(() => {
  // hintDelay staggers the two teams so they don't fan out in perfect lockstep
  const out = 500 + (props.hintDelay ?? 0)
  hintOutTimer = window.setTimeout(() => { if (!props.open) hintOpenRef.value = true }, out)
  hintInTimer = window.setTimeout(() => { hintOpenRef.value = false }, out + 700)
})

onUnmounted(() => {
  window.clearTimeout(hintOutTimer)
  window.clearTimeout(hintInTimer)
})

// keep the rendered swatches in sync when the colour names resolve after the set is shown
watch(colorSetRef, (set) => {
  if (altsRef.value.length === set.length) {
    altsRef.value = altsRef.value.map((s, i) => ({ ...s, color: set[i].color, name: set[i].name }))
  }
})

function toggleWithSound() {
  if (props.open) {
    sounds.collapse()
  } else {
    sounds.expand()
  }
  emit('toggle')
}

// tapping a swatch simply sets the team's colour and name — no swapping
function pick(i: number) {
  sounds.colorChange()
  const chosen = colorSetRef.value[i]
  emit('update:color', chosen.color)
  emit('update:name', chosen.name)
}

async function roll() {
  if (rollingRef.value) {
    return
  }
  rollingRef.value = true

  sounds.collapse()
  altsRef.value = altsRef.value.map(s => ({ ...s, transform: stackedTransform }))

  const colors = Array.from({ length: 8 }, () => randomColor())
  const [names] = await Promise.all([fetchColorNames(colors), new Promise(resolve => setTimeout(resolve, 400))])

  altsRef.value = []
  colorSetRef.value = colors.map((color, i) => ({ color, name: names[i] }))
  emit('update:color', colors[0])
  emit('update:name', names[0])

  await new Promise(resolve => setTimeout(resolve, 50))
  sounds.expand()
  showAlts()

  rollingRef.value = false
}
</script>
